import { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { WalletButton } from './WalletButton';
import { CartButton } from './CartButton';
import { getCollectionsByNetwork } from '@/lib/collections';
import { useTheme } from '@/hooks/useTheme.tsx';
import { useAppNetwork } from '@/hooks/useAppNetwork';
import { useWallet } from '@/hooks/useWallet';

export function Header() {
  const [collectionsOpen, setCollectionsOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const mobileMenuRef = useRef<HTMLDivElement>(null);
  const mobileMenuButtonRef = useRef<HTMLButtonElement>(null);
  const navigate = useNavigate();
  const { network: appNetwork } = useAppNetwork();
  const collections = getCollectionsByNetwork(appNetwork);
  const { theme, toggleTheme } = useTheme();
  const { isConnected, address } = useWallet();

  // Navigate to collection without any query params (clears filters)
  const navigateToCollection = (slug: string) => {
    setCollectionsOpen(false);
    setMobileMenuOpen(false);
    navigate(`/collection/${slug}`);
  };

  // Close dropdowns when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setCollectionsOpen(false);
      }
      // Don't close mobile menu if clicking the toggle button (let onClick handle it)
      if (
        mobileMenuRef.current &&
        !mobileMenuRef.current.contains(event.target as Node) &&
        !mobileMenuButtonRef.current?.contains(event.target as Node)
      ) {
        setMobileMenuOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <header className="border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900">
      <div className="container mx-auto px-4 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-8">
            <Link to="/" className="text-2xl font-bold text-gray-900 dark:text-white">
              Tezos Marketplace
            </Link>
            <nav className="hidden md:flex items-center gap-6">
              {/* Collections Dropdown */}
              <div className="relative" ref={dropdownRef}>
                <button
                  onClick={() => setCollectionsOpen(!collectionsOpen)}
                  className="flex items-center gap-1 text-gray-700 dark:text-gray-200 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                >
                  Collections
                  <svg
                    className={`w-4 h-4 transition-transform ${collectionsOpen ? 'rotate-180' : ''}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                {collectionsOpen && (
                  <div className="absolute top-full left-0 mt-2 w-56 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 py-2 z-50">
                    {collections.map((collection) => (
                      <button
                        key={collection.slug}
                        onClick={() => navigateToCollection(collection.slug)}
                        className="flex items-center gap-3 px-4 py-2 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors w-full text-left"
                      >
                        <img
                          src={collection.thumbnail}
                          alt={collection.name}
                          className="w-8 h-8 rounded-full object-cover"
                          onError={(e) => {
                            (e.target as HTMLImageElement).src = `https://ui-avatars.com/api/?name=${collection.name}&size=32&background=random`;
                          }}
                        />
                        <span className="font-medium text-gray-900 dark:text-gray-100">{collection.name}</span>
                      </button>
                    ))}
                    <div className="border-t border-gray-100 dark:border-gray-700 mt-2 pt-2">
                      <Link
                        to="/collections"
                        onClick={() => setCollectionsOpen(false)}
                        className="block px-4 py-2 text-sm text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                      >
                        View All Collections
                      </Link>
                    </div>
                  </div>
                )}
              </div>

            </nav>
          </div>
          <div className="flex items-center gap-4">
            <button
              onClick={toggleTheme}
              className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
              aria-label={`Switch to ${theme === 'light' ? 'dark' : 'light'} mode`}
            >
              {theme === 'light' ? (
                <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
                </svg>
              ) : (
                <svg className="w-5 h-5 text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
                </svg>
              )}
            </button>
            <CartButton />
            {isConnected && address && (
              <Link
                to="/my-collection"
                className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                title="My Collection"
              >
                <svg
                  className="w-5 h-5 text-gray-600 dark:text-gray-300"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5.121 17.804A13.937 13.937 0 0112 16c2.5 0 4.847.655 6.879 1.804M15 10a3 3 0 11-6 0 3 3 0 016 0zm6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </Link>
            )}
            <div className="hidden md:block">
              <WalletButton />
            </div>
            {/* Mobile menu button */}
            <button
              ref={mobileMenuButtonRef}
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
              aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
            >
              {mobileMenuOpen ? (
                <svg className="w-6 h-6 text-gray-600 dark:text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              ) : (
                <svg className="w-6 h-6 text-gray-600 dark:text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              )}
            </button>
          </div>
        </div>

        {/* Mobile menu panel */}
        {mobileMenuOpen && (
          <div ref={mobileMenuRef} className="md:hidden border-t border-gray-200 dark:border-gray-700 mt-4 pt-4">
            <nav className="flex flex-col gap-2">
              {/* Collections section */}
              <div className="text-sm font-semibold text-gray-500 dark:text-gray-400 px-2 mb-1">
                Collections
              </div>
              {collections.map((collection) => (
                <button
                  key={collection.slug}
                  onClick={() => navigateToCollection(collection.slug)}
                  className="flex items-center gap-3 px-2 py-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors w-full text-left"
                >
                  <img
                    src={collection.thumbnail}
                    alt={collection.name}
                    className="w-8 h-8 rounded-full object-cover"
                    onError={(e) => {
                      (e.target as HTMLImageElement).src = `https://ui-avatars.com/api/?name=${collection.name}&size=32&background=random`;
                    }}
                  />
                  <span className="font-medium text-gray-900 dark:text-gray-100">{collection.name}</span>
                </button>
              ))}
              <Link
                to="/collections"
                onClick={() => setMobileMenuOpen(false)}
                className="px-2 py-2 text-sm text-gray-600 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
              >
                View All Collections →
              </Link>

              <div className="border-t border-gray-200 dark:border-gray-700 my-2"></div>

              {/* Wallet button in mobile menu */}
              <div className="px-2 py-2">
                <WalletButton />
              </div>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
}
